/**
 * StorageService - Avatar-Scoped Local Storage Management
 *
 */

import {
  STORAGE_KEYS,
  CURRENT_MIGRATION_VERSION,
  MAX_AVATARS,
  RETENTION_POLICIES,
  LEGACY_KEYS_TO_REMOVE,
  LOGOUT_EXCLUSION_KEYS,
  AVATAR_SCOPED_KEYS,
  JSON_KEYS,
} from '@/constants/storage';

class StorageService {
  constructor() {
    this.migrateIfNeeded();
  }

  /**
   * Get scoped key for avatar-specific storage
   * @param {string} baseKey - Base key from STORAGE_KEYS
   * @param {string|null} avatarId - Avatar ID for scoping, null for global keys
   * @returns {string} Fully scoped key
   */
  getScopedKey(baseKey, avatarId = null) {
    if (AVATAR_SCOPED_KEYS.includes(baseKey) && !avatarId) {
      throw new Error(`Avatar ID required for scoped key: ${baseKey}`);
    }

    return avatarId ? `${baseKey}${avatarId}` : baseKey;
  }

  /**
   * Set item in localStorage with avatar scoping and retention policy
   * @param {string} key - Base key from STORAGE_KEYS
   * @param {any} value - Value to store
   * @param {string|null} avatarId - Avatar ID for scoping
   * @param {string|null} policy - Retention policy
   */
  setItem(key, value, avatarId = null, policy = null) {
    try {
      const fullKey = this.getScopedKey(key, avatarId);
      const serializedValue =
        typeof value === 'string' ? value : JSON.stringify(value);

      localStorage.setItem(fullKey, serializedValue);

      // enforce retention policies
      this.enforceRetention(fullKey, policy, avatarId);
    } catch (error) {
      console.error('StorageService: Failed to set item', {
        key,
        avatarId,
        error,
      });
    }
  }

  /**
   * Get item from localStorage with avatar scoping
   * @param {string} key - Base key from STORAGE_KEYS
   * @param {string|null} avatarId - Avatar ID for scoping
   * @param {any} defaultValue - Default value if not found
   * @returns {any} Retrieved value or default
   */
  getItem(key, avatarId = null, defaultValue = null) {
    try {
      const fullKey = this.getScopedKey(key, avatarId);
      const item = localStorage.getItem(fullKey);

      if (item === null) {
        return defaultValue;
      }

      // auto-parse JSON for known object keys
      if (JSON_KEYS.includes(key) || key.endsWith('Params')) {
        return JSON.parse(item);
      }

      return item;
    } catch (error) {
      return defaultValue;
    }
  }

  /**
   * Remove item from localStorage with avatar scoping
   * @param {string} key - Base key from STORAGE_KEYS
   * @param {string|null} avatarId - Avatar ID for scoping
   */
  removeItem(key, avatarId = null) {
    try {
      const fullKey = this.getScopedKey(key, avatarId);
      localStorage.removeItem(fullKey);
    } catch (error) {
      console.error('StorageService: Failed to remove item', {
        key,
        avatarId,
        error,
      });
    }
  }

  /**
   * Removes all localStorage keys except those required to persist
   */
  performLogoutCleanup() {
    Object.keys(localStorage).forEach((key) => {
      const shouldPreserve = LOGOUT_EXCLUSION_KEYS.some((excludeKey) =>
        key.startsWith(excludeKey)
      );

      if (!shouldPreserve) {
        localStorage.removeItem(key);
      }
    });
  }

  /**
   * Clear storage on avatar switch
   * @param {number} newAvatarId - New avatar ID
   */
  clearOnAvatarSwitch(newAvatarId) {
    this.removeItem(STORAGE_KEYS.MULTICAM_LAYOUT);
    this.removeItem(STORAGE_KEYS.CLIP_LIST_POSITION);
    this.removeItem(STORAGE_KEYS.CLIP_SEARCH_PARAMS);
    this.removeItem(STORAGE_KEYS.LastVideoClipsSearchParams);

    this.updateRecentAvatars(newAvatarId);
  }

  /**
   * Update recent avatars list
   * @param {number} newAvatarId - New avatar ID
   */
  updateRecentAvatars(newAvatarId) {
    let recent = this.getItem(STORAGE_KEYS.RECENT_AVATARS, null, []);

    // remove existing null values to ensure only single null value is allowed
    recent = recent.filter((id) => id !== null);

    // convert to numbers for consistent comparison
    const newId = newAvatarId === null ? null : Number(newAvatarId);

    // if avatar is already the first (most recent), no need to update
    if (recent.length > 0 && recent[0] === newId) {
      return;
    }

    // remove new avatar if it exists elsewhere in the list (to avoid duplicates)
    recent = recent.filter((id) => id !== newId);

    // add the new avatar to the front (most recent first)
    recent.unshift(newId);

    // enforce avatar limit and track removed avatars
    // null values are exempt from the MAX_AVATARS limit
    let removedAvatars = [];
    const nonNullAvatars = recent.filter((id) => id !== null);
    if (nonNullAvatars.length > MAX_AVATARS) {
      // keep null values and remove excess non-null avatars from the end
      const nullValues = recent.filter((id) => id === null);
      recent = [...nullValues, ...nonNullAvatars.slice(0, MAX_AVATARS)];
      removedAvatars = nonNullAvatars.slice(MAX_AVATARS);
    }

    this.setItem(
      STORAGE_KEYS.RECENT_AVATARS,
      recent,
      null,
      RETENTION_POLICIES.GLOBAL
    );

    if (removedAvatars.length > 0) {
      this.cleanupRemovedAvatarKeys(removedAvatars);
    }
  }

  /**
   * Enforce retention policies for stored items
   * @param {string} fullKey - Full storage key
   * @param {string} policy - Retention policy
   * @param {string} avatarId - Avatar ID
   */
  enforceRetention(fullKey, policy, avatarId) {
    if (policy === RETENTION_POLICIES.MULTI_AVATAR_PERSISTENT && avatarId) {
      this.enforceAvatarLimit();
    }
  }

  /**
   * Enforce avatar limit
   */
  enforceAvatarLimit() {
    const bKeys = Object.keys(localStorage).filter((key) =>
      key.startsWith(STORAGE_KEYS.CAMERA_LIST_SETTING)
    );

    if (bKeys.length > MAX_AVATARS) {
      const avatarIds = bKeys.map((key) => {
        const parts = key.split('_');
        return Number(parts[parts.length - 1]);
      });

      const avatarsToRemove = avatarIds.slice(MAX_AVATARS);

      this.cleanupRemovedAvatarKeys(avatarsToRemove);
    }
  }

  /**
   * Clean up all avatar-scoped keys for removed avatars
   * @param {Array} removedAvatarIds - Avatar IDs that were removed from recent avatars
   */
  cleanupRemovedAvatarKeys(removedAvatarIds) {
    if (!removedAvatarIds || removedAvatarIds.length === 0) {
      return;
    }

    removedAvatarIds.forEach((avatarId) => {
      AVATAR_SCOPED_KEYS.forEach((baseKey) => {
        this.removeItem(baseKey, avatarId);
      });
    });
  }

  /**
   * Remove orphaned avatar-scoped keys that don't exist in recent avatars list
   * This cleans up keys left behind from previous sessions or bugs
   */
  cleanupOrphanedAvatarKeys() {
    const recentAvatars = this.getItem(STORAGE_KEYS.RECENT_AVATARS, null, []);
    const recentAvatarIds = new Set(recentAvatars.map((id) => Number(id)));

    const orphanedAvatarIds = new Set();

    // find all avatar IDs that have scoped keys in localStorage
    AVATAR_SCOPED_KEYS.forEach((baseKey) => {
      Object.keys(localStorage).forEach((storageKey) => {
        if (storageKey.startsWith(baseKey)) {
          const avatarIdStr = storageKey.replace(baseKey, '');
          const avatarId = Number(avatarIdStr);

          if (avatarId && !isNaN(avatarId) && !recentAvatarIds.has(avatarId)) {
            orphanedAvatarIds.add(avatarId);
          }
        }
      });
    });

    if (orphanedAvatarIds.size > 0) {
      const orphanedIds = Array.from(orphanedAvatarIds);
      this.cleanupRemovedAvatarKeys(orphanedIds);
    }
  }

  /**
   * Check if migration is needed and perform it
   */
  migrateIfNeeded() {
    const version = localStorage.getItem(STORAGE_KEYS.MIGRATION_VERSION);

    if (!version || version !== CURRENT_MIGRATION_VERSION) {
      this.performMigration();
      localStorage.setItem(
        STORAGE_KEYS.MIGRATION_VERSION,
        CURRENT_MIGRATION_VERSION
      );
    }
  }

  /**
   * Perform migration
   */
  performMigration() {
    this.removeLegacyKeys();
    this.migrateUserScopedKeys();
    this.migrateCurrentAvatarToRecentAvatars();
    this.cleanupOrphanedAvatarKeys();
  }

  /**
   * Remove legacy keys
   */
  removeLegacyKeys() {
    const keysToRemove = [];
    const allKeys = Object.keys(localStorage);

    allKeys.forEach((key) => {
      // only remove keys that are explicitly listed in LEGACY_KEYS_TO_REMOVE
      if (LEGACY_KEYS_TO_REMOVE.includes(key)) {
        keysToRemove.push(key);
        return;
      }

      // check for pattern matches (keys ending with _)
      const patternMatches = LEGACY_KEYS_TO_REMOVE.filter((pattern) => {
        if (typeof pattern === 'string' && pattern.endsWith('_')) {
          return key.startsWith(pattern);
        }
        return false;
      });

      if (patternMatches.length > 0) {
        keysToRemove.push(key);
        return;
      }
    });

    keysToRemove.forEach((key) => {
      localStorage.removeItem(key);
    });
  }

  /**
   * Migrate user-scoped keys to avatar-scoped keys
   */
  migrateUserScopedKeys() {
    // initialize recent avatars if it doesn't exist
    const existingRecentAvatars = this.getItem(STORAGE_KEYS.RECENT_AVATARS);
    if (existingRecentAvatars === null || existingRecentAvatars === undefined) {
      this.setItem(
        STORAGE_KEYS.RECENT_AVATARS,
        [],
        null,
        RETENTION_POLICIES.GLOBAL
      );
    }

    // extract avatar IDs from existing avatar-scoped keys
    this.populateRecentAvatarsFromExistingKeys();
  }

  /**
   * Ensure recent avatars are populated when avatar data becomes available
   * This should be called after authentication when avatar data is loaded
   */
  ensureRecentAvatarsPopulated() {
    // check if recent avatars already has data
    const currentRecentAvatars = this.getItem(
      STORAGE_KEYS.RECENT_AVATARS,
      null,
      []
    );

    if (currentRecentAvatars.length > 0) {
      // Clean up orphaned keys even if recent avatars exist
      this.cleanupOrphanedAvatarKeys();
      return;
    }

    // try to populate from existing keys
    this.populateRecentAvatarsFromExistingKeys();

    this.cleanupOrphanedAvatarKeys();
  }

  /**
   * Populate recent avatars from existing avatar-scoped keys
   */
  populateRecentAvatarsFromExistingKeys() {
    const existingAvatars = new Set();

    // look for avatar IDs in existing keys
    Object.keys(localStorage).forEach((key) => {
      // check for cameraListSetting_<avatarId> keys
      if (key.startsWith(STORAGE_KEYS.CAMERA_LIST_SETTING)) {
        const avatarIdStr = key.replace(STORAGE_KEYS.CAMERA_LIST_SETTING, '');
        const avatarId = Number(avatarIdStr);
        if (avatarId && !isNaN(avatarId)) {
          existingAvatars.add(avatarId);
        }
      }

      // check for selectedCurrentHomeGroup_<avatarId> keys
      if (key.startsWith(STORAGE_KEYS.SELECTED_HOME_GROUP)) {
        const avatarIdStr = key.replace(STORAGE_KEYS.SELECTED_HOME_GROUP, '');
        const avatarId = Number(avatarIdStr);
        if (avatarId && !isNaN(avatarId)) {
          existingAvatars.add(avatarId);
        }
      }
    });

    // update recent avatars with found avatar IDs
    if (existingAvatars.size > 0) {
      const recentAvatars = Array.from(existingAvatars).slice(0, MAX_AVATARS);

      this.setItem(
        STORAGE_KEYS.RECENT_AVATARS,
        recentAvatars,
        null,
        RETENTION_POLICIES.GLOBAL
      );
    }
  }

  /**
   * Get recent avatars list
   * @returns {Array} Array of recent avatar IDs
   */
  getRecentAvatars() {
    return this.getItem(STORAGE_KEYS.RECENT_AVATARS, null, []);
  }

  /**
   * Migrate CurrentAvatar key to recent avatars system
   */
  migrateCurrentAvatarToRecentAvatars() {
    const currentAvatarKey = 'secom_current_avatar';
    const currentAvatarIdStr = localStorage.getItem(currentAvatarKey);

    if (currentAvatarIdStr) {
      const currentAvatarId = Number(currentAvatarIdStr);

      let recentAvatars = this.getRecentAvatars();

      // add the current avatar to the front if it's not already there
      if (!recentAvatars.includes(currentAvatarId)) {
        recentAvatars = [currentAvatarId, ...recentAvatars];
        this.setItem(STORAGE_KEYS.RECENT_AVATARS, recentAvatars);
      }

      localStorage.removeItem(currentAvatarKey);
    }
  }
}

export default new StorageService();
